<template>
  <Layout>
    <template #content-left>
      <div class="px-4 px-md-0 mb-3">
        <router-link :to="{ name: 'home' }" class="text-gray">
          <Icon name="back" size="22" class="v-align-middle" />
          Home
        </router-link>
      </div>
      <div class="px-4 px-md-0">
        <h1 v-if="loaded" v-text="'Settings'" class="mb-4" />
        <PageLoading v-else />
      </div>
      <template v-if="loaded">
        <div v-if="isReady">
          <Block title="Profile">
            <div class="mb-2">
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Name</div>
                <input v-model="form.name" class="input flex-auto" required />
              </UiButton>
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Network</div>
                <div class="flex-auto">
                  {{ form.network }}
                </div>
              </UiButton>
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Symbol</div>
                <input v-model="form.symbol" class="input flex-auto" required />
              </UiButton>
              <UiButton
                @click="modalSkinsOpen = true"
                class="text-left width-full mb-2 d-flex px-3"
              >
                <div class="text-gray mr-2">Skin</div>
                <div class="flex-auto">
                  {{ form.skin ? form.skin : 'Default skin' }}
                </div>
              </UiButton>
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Domain name</div>
                <input v-model="form.domain" class="input flex-auto" />
                <a
                  class="d-block py-1 mr-n2"
                  target="_blank"
                  href="https://docs.snapshot.page/spaces/add-custom-domain"
                >
                  <Icon name="info" size="24" class="text-gray p-1" />
                </a>
              </UiButton>
            </div>
          </Block>
          <Block title="Strategies">
            <div
              v-for="(strategy, i) in form.strategies"
              :key="i"
              class="mb-3 position-relative"
            >
              <a
                @click="handleRemoveStrategy(i)"
                class="position-absolute p-4 right-0"
              >
                <Icon name="close" size="12" />
              </a>
              <a
                @click="handleEditStrategy(i)"
                class="p-4 d-block border rounded-2"
              >
                <h4 v-text="strategy.name" />
              </a>
            </div>
            <UiButton @click="handleAddStrategy" class="d-block width-full">
              Add strategy
            </UiButton>
          </Block>
          <Block title="Members">
            <UiButton class="d-block width-full px-3" style="height: auto;">
              <TextareaArray
                v-model="form.members"
                :placeholder="
                  `0x8C28Cf33d9Fd3D0293f963b1cd27e3FF422B425c\n0xeF8305E140ac520225DAf050e2f71d5fBcC543e7`
                "
                class="input width-full text-left"
                style="font-size: 18px;"
              />
            </UiButton>
          </Block>
          <Block title="Filters">
            <div class="mb-2">
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Default tab</div>
                <input
                  v-model="form.filters.defaultTab"
                  class="input flex-auto"
                />
              </UiButton>
              <UiButton class="text-left width-full mb-2 d-flex px-3">
                <div class="text-gray mr-2">Min. score</div>
                <div class="flex-auto">
                  <InputNumber v-model="form.filters.minScore" class="input" />
                </div>
              </UiButton>
              <div class="mb-2 d-flex flex-items-center px-2">
                <Checkbox
                  v-model="form.filters.onlyMembers"
                  class="mr-2 mt-1"
                />
                Show only members proposals
              </div>
              <UiButton class="d-block width-full px-3" style="height: auto;">
                <TextareaArray
                  v-model="form.filters.invalids"
                  :placeholder="
                    `Invalids proposals\nQmc4VSHwY3SVmo4oofhL2qDPaYcGaQqndM4oqdQQe2aZHQ\nQmTMAgnPy2q6LRMNwvj27PHvWEgZ3bw7yTtNNEucBZCWhZ`
                  "
                  class="input width-full text-left"
                  style="font-size: 18px;"
                />
              </UiButton>
            </div>
          </Block>
          <Block
            v-if="form.plugins && Object.keys(form.plugins)"
            title="Plugins"
          >
            <div v-for="(plugin, i) in form.plugins" :key="i" class="mb-2">
              <div class="p-4 d-block border rounded-2">
                <h4 v-text="i" />
              </div>
            </div>
          </Block>
        </div>
      </template>
    </template>
    <template v-if="loaded && isReady" #sidebar-right>
      <Block title="Actions">
        <UiButton @click="handleReset" class="d-block width-full mb-2">
          Reset
        </UiButton>
        <UiButton
          @click="handleSubmit"
          :disabled="!isValid"
          :loading="loading"
          class="d-block width-full button--submit"
        >
          Save
        </UiButton>
      </Block>
    </template>
  </Layout>
  <teleport to="#modal">
    <ModalNetworks
      :open="modalNetworksOpen"
      @close="modalNetworksOpen = false"
      v-model="form.network"
    />
    <ModalSkins
      :open="modalSkinsOpen"
      @close="modalSkinsOpen = false"
      v-model="form.skin"
    />
    <ModalStrategy
      :open="modalStrategyOpen"
      @close="modalStrategyOpen = false"
      @add="handleSubmitAddStrategy"
      :strategy="currentStrategy"
      :strategyIndex="currentStrategyIndex"
    />
  </teleport>
</template>

<script>
import { mapActions } from 'vuex';
import { validateSchema } from '@snapshot-labs/snapshot.js/src/utils';
import schemas from '@snapshot-labs/snapshot.js/src/schemas';
import networks from '@snapshot-labs/snapshot.js/src/networks.json';
import { clone } from '@/helpers/utils';
// const gateway = process.env.VUE_APP_IPFS_GATEWAY || gateways[0];

export default {
  data() {
    return {
      key: this.$route.params.key,
      from: this.$route.params.from,
      currentSettings: {},
      currentContenthash: '',
      currentStrategy: {},
      currentStrategyIndex: false,
      modalNetworksOpen: false,
      modalSkinsOpen: false,
      modalStrategyOpen: false,
      loaded: false,
      loading: false,
      form: {
        strategies: [],
        filters: {},
        network: 'testnet.iotex'
      },
      networks
    };
  },
  computed: {
    validate() {
      return validateSchema(schemas.space, this.form);
    },
    isValid() {
      if (this.validate !== true) console.log(this.validate);
      return !this.loading && this.web3.account && this.validate === true;
    },
    contenthash() {
      const address = this.web3.account;
      return `ipns://storage.snapshot.page/registry/${address}/${this.key}`;
    },
    isReady() {
      // return this.currentContenthash === this.contenthash;
      return true;
    }
  },
  async created() {
    try {
      const uri = `https://${this.web3.account}`;
      this.currentContenthash = uri;
      let space = clone(this.app.spaces?.[this.key]);
      if (!space) space = {};
      delete space.key;
      delete space._activeProposals;
      space.filters = space.filters || {};
      space.strategies = space.strategies || [];
      this.currentSettings = clone(space);
      this.form = space;
    } catch (e) {
      console.log(e);
    }
    if (this.from) {
      const from = clone(this.app.spaces[this.from]);
      delete from.key;
      delete from._activeProposals;
      this.form = from;
    }
    this.loaded = true;
  },
  methods: {
    ...mapActions(['notify', 'send', 'getSpaces', 'login']),
    async handleSubmit() {
      this.loading = true;
      try {
        await this.send({
          space: this.key,
          type: 'settings',
          payload: this.form
        });
      } catch (e) {
        console.log(e);
      }
      await this.getSpaces();
      this.loading = false;
    },
    handleReset() {
      if (this.from) return (this.form = clone(this.app.spaces[this.from]));
      if (this.currentSettings) return (this.form = this.currentSettings);
      this.form = {
        strategies: [],
        filters: {}
      };
    },
    handleCopy() {
      this.notify('Copied!');
    },
    handleEditStrategy(i) {
      this.currentStrategyIndex = i;
      this.currentStrategy = clone(this.form.strategies[i]);
      this.modalStrategyOpen = true;
    },
    handleRemoveStrategy(i) {
      this.form.strategies = this.form.strategies.filter(
        (strategy, index) => index !== i
      );
    },
    handleAddStrategy() {
      this.currentStrategyIndex = false;
      this.currentStrategy = {};
      this.modalStrategyOpen = true;
    },
    handleSubmitAddStrategy(strategy) {
      if (this.currentStrategyIndex !== false) {
        this.form.strategies[this.currentStrategyIndex] = strategy;
      } else {
        this.form.strategies = this.form.strategies.concat(strategy);
      }
    }
  }
};
</script>
